/**
 * Pure date maths for the "go to date" popup on the chart's time axis.
 *
 * The chart itself is not unit-testable (canvas, gestures, a live socket), but
 * everything the picker actually gets wrong is arithmetic: which days a range
 * covers, where a picked day lands on the time scale, and whether that landing
 * point is already loaded. All of that lives here as plain functions.
 *
 * ## Timezone
 *
 * Every time label the chart draws is formatted in `America/Los_Angeles`
 * (`localization.timeFormatter` and `timeScale.tickMarkFormatter` in
 * ChartView). A picker that computed days in the browser's local zone would
 * disagree with the axis it is anchored to for anyone not sitting in PT —
 * "Aug 13" in the calendar would enable a day the axis calls Aug 12. So day
 * boundaries here are the chart's boundaries, not the browser's.
 *
 * `Intl` does the zone work rather than a date library: the app has no
 * general-purpose date dependency, and the only two operations needed are
 * "which day is this instant in that zone" and its inverse.
 */

/** The zone every chart time label is rendered in. Day boundaries follow it. */
export const CHART_TIME_ZONE = 'America/Los_Angeles';

/** A calendar day as `YYYY-MM-DD`, always in {@link CHART_TIME_ZONE}. */
export type DayKey = string;

/**
 * Tick coverage reported by `GET /api/candles/range`. Note this is TICK
 * coverage, not bar coverage — deliberate upstream, and correct here because
 * the picker only ever resolves to day granularity.
 */
export interface DataRange {
  first: number;
  last: number;
}

/** The span of the loaded candle series, in epoch ms. */
export interface LoadedSpan {
  first: number;
  last: number;
}

const dayFormatters = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  let fmt = dayFormatters.get(timeZone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    dayFormatters.set(timeZone, fmt);
  }
  return fmt;
}

function zonedParts(ms: number, timeZone: string) {
  const parts = partsFormatter(timeZone).formatToParts(new Date(ms));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  // `h23` should never produce 24, but Safari has historically emitted it for
  // midnight; folding it to 0 costs nothing and avoids a one-day-off offset.
  const hour = get('hour') % 24;
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour,
    minute: get('minute'),
    second: get('second'),
  };
}

/** Zone offset at a given instant, in ms (positive east of UTC). */
function zoneOffsetMs(ms: number, timeZone: string): number {
  const p = zonedParts(ms, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - ms;
}

/** `YYYY-MM-DD` for the given calendar fields. */
export function formatDayKey(year: number, month: number, day: number): DayKey {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Split a `YYYY-MM-DD` key back into numeric fields. */
export function parseDayKey(day: DayKey): { year: number; month: number; day: number } {
  const [year, month, dayOfMonth] = day.split('-').map(Number);
  return { year, month, day: dayOfMonth };
}

/** Which calendar day an instant falls on, in the chart's zone. */
export function dayKeyOf(ms: number, timeZone: string = CHART_TIME_ZONE): DayKey {
  const p = zonedParts(ms, timeZone);
  return formatDayKey(p.year, p.month, p.day);
}

/**
 * Epoch ms of midnight at the start of `day` in `timeZone`.
 *
 * Solved rather than looked up: guess with the UTC offset of the wall-clock
 * instant, then re-measure the offset AT that guess and re-apply it. The second
 * pass is what makes DST transition days correct — on a spring-forward day the
 * first guess sits an hour off, and measuring there gives the right offset.
 */
export function startOfDayMs(day: DayKey, timeZone: string = CHART_TIME_ZONE): number {
  const { year, month, day: dayOfMonth } = parseDayKey(day);
  const wallClock = Date.UTC(year, month - 1, dayOfMonth, 0, 0, 0);
  const firstGuess = wallClock - zoneOffsetMs(wallClock, timeZone);
  return wallClock - zoneOffsetMs(firstGuess, timeZone);
}

/** Last millisecond of `day` in `timeZone`. */
export function endOfDayMs(day: DayKey, timeZone: string = CHART_TIME_ZONE): number {
  return startOfDayMs(addDays(day, 1), timeZone) - 1;
}

/** Calendar-day arithmetic. Zone-independent — a date is a date. */
export function addDays(day: DayKey, delta: number): DayKey {
  const { year, month, day: dayOfMonth } = parseDayKey(day);
  const shifted = new Date(Date.UTC(year, month - 1, dayOfMonth + delta));
  return formatDayKey(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate());
}

/**
 * The inclusive first/last enabled day for a range, or `null` when there is no
 * range at all.
 *
 * `null` is the honest answer for a 404, an empty `.scid`, or a failed request,
 * and the caller must render it as "nothing selectable". Falling back to the
 * loaded candles or to "today minus N" would light up days the picker cannot
 * actually jump to.
 */
export function enabledDayBounds(
  range: DataRange | null | undefined,
  timeZone: string = CHART_TIME_ZONE
): { first: DayKey; last: DayKey } | null {
  if (!range) return null;
  const { first, last } = range;
  if (!Number.isFinite(first) || !Number.isFinite(last) || last < first) return null;
  return { first: dayKeyOf(first, timeZone), last: dayKeyOf(last, timeZone) };
}

/**
 * True when `day` overlaps the covered range by even one millisecond.
 *
 * Overlap, not containment: the first and last days of coverage are usually
 * partial (the file starts mid-session) and are still perfectly jumpable.
 */
export function isDayEnabled(
  day: DayKey,
  range: DataRange | null | undefined,
  timeZone: string = CHART_TIME_ZONE
): boolean {
  const bounds = enabledDayBounds(range, timeZone);
  if (!bounds || !range) return false;
  return day >= bounds.first && day <= bounds.last;
}

/**
 * Where the chart should land for a picked day: the day's midnight, clamped
 * into the covered range.
 *
 * The clamp matters at both ends. Coverage typically starts mid-session, so
 * midnight on the first available day is before any data exists; landing on
 * `range.first` instead puts the user on the first real bar rather than in
 * empty space that reads like a broken jump.
 */
export function targetTimestampForDay(
  day: DayKey,
  range: DataRange,
  timeZone: string = CHART_TIME_ZONE
): number {
  const midnight = startOfDayMs(day, timeZone);
  if (midnight < range.first) return range.first;
  if (midnight > range.last) return range.last;
  return midnight;
}

/**
 * What picking `day` should actually do.
 *
 * - `scroll` — the target is inside the loaded series; just move the viewport.
 * - `fetch` — the target predates the oldest loaded bar; page history back
 *   first, then scroll.
 * - `unavailable` — no range, or a day outside it. Should be unreachable from
 *   the UI (those days render disabled) but is the safe answer if it isn't.
 *
 * A target NEWER than the loaded series resolves to `scroll`: the newest loaded
 * bar is the live edge, there is no forward-fetch path, and the caller clamps
 * to the end of the series rather than pretending it can go further.
 */
export type PickResolution =
  | { kind: 'scroll'; timestamp: number }
  | { kind: 'fetch'; timestamp: number }
  | { kind: 'unavailable' };

export function resolvePick(
  day: DayKey,
  range: DataRange | null | undefined,
  loaded: LoadedSpan | null | undefined,
  timeZone: string = CHART_TIME_ZONE
): PickResolution {
  if (!range || !isDayEnabled(day, range, timeZone)) return { kind: 'unavailable' };
  const timestamp = targetTimestampForDay(day, range, timeZone);
  if (!loaded) return { kind: 'fetch', timestamp };
  if (timestamp < loaded.first) return { kind: 'fetch', timestamp };
  return { kind: 'scroll', timestamp };
}

/**
 * A calendar month laid out as whole weeks starting Sunday. `null` cells are
 * the leading/trailing padding, rendered as blanks rather than as neighbouring
 * months' days — a greyed-out 31st that belongs to another month is the classic
 * way to get a mis-click in a picker where most days are already disabled.
 */
export function buildMonthGrid(year: number, month: number): (DayKey | null)[] {
  const firstWeekday = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const cells: (DayKey | null)[] = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(formatDayKey(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

/** Month arithmetic that rolls the year over, for the `<`/`>` header buttons. */
export function shiftMonth(
  year: number,
  month: number,
  delta: number
): { year: number; month: number } {
  const zeroBased = year * 12 + (month - 1) + delta;
  return { year: Math.floor(zeroBased / 12), month: (zeroBased % 12) + 1 };
}

/**
 * True when a month contains no enabled day at all, so the header can disable
 * the arrow that would step into dead space instead of letting the user page
 * through empty months looking for data.
 */
export function monthHasEnabledDay(
  year: number,
  month: number,
  range: DataRange | null | undefined,
  timeZone: string = CHART_TIME_ZONE
): boolean {
  const bounds = enabledDayBounds(range, timeZone);
  if (!bounds) return false;
  const monthFirst = formatDayKey(year, month, 1);
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const monthLast = formatDayKey(year, month, daysInMonth);
  return monthFirst <= bounds.last && monthLast >= bounds.first;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** `August 2026` for the popup header. */
export function monthLabel(year: number, month: number): string {
  return `${MONTH_NAMES[month - 1]} ${year}`;
}

/**
 * Which month the picker should open on: the month of `focusMs` (normally the
 * right edge of what the user is currently looking at) if it has any data,
 * otherwise the month holding the newest available data. Opening on an empty
 * month would make a working picker look broken.
 */
export function initialMonth(
  range: DataRange | null | undefined,
  focusMs: number | null | undefined,
  timeZone: string = CHART_TIME_ZONE
): { year: number; month: number } {
  const bounds = enabledDayBounds(range, timeZone);
  if (bounds && focusMs != null && Number.isFinite(focusMs)) {
    const focus = parseDayKey(dayKeyOf(focusMs, timeZone));
    if (monthHasEnabledDay(focus.year, focus.month, range, timeZone)) {
      return { year: focus.year, month: focus.month };
    }
  }
  if (bounds) {
    const last = parseDayKey(bounds.last);
    return { year: last.year, month: last.month };
  }
  const now = focusMs != null && Number.isFinite(focusMs) ? focusMs : Date.now();
  const today = parseDayKey(dayKeyOf(now, timeZone));
  return { year: today.year, month: today.month };
}

/** `Aug 13, 2026` — used for the range footer and progress copy. */
export function formatDayLabel(day: DayKey, style: 'short' | 'long' = 'short'): string {
  const { year, month, day: dayOfMonth } = parseDayKey(day);
  const name = MONTH_NAMES[month - 1];
  const shortName = style === 'short' ? name.slice(0, 3) : name;
  return `${shortName} ${dayOfMonth}, ${year}`;
}
